import { EventType } from "@/analytics/analytics-events";
import { ANNOTATION_ZINDEX_RANGE_MAP } from "@/components/ui/annotations/annotation-zindex-range-map";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import {
  selectIsNewPin,
  selectPinMarker,
  setIsNewPin,
} from "@/store/pin-marker-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { PinColoredIcon } from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import Box from "@mui/material/Box";
import { Html } from "@react-three/drei";
import { RefObject, useCallback, useEffect, useState, type JSX } from "react";
import { Vector3Tuple } from "three";
import { PinMarkerActionBar } from "./pin-marker-action-bar";

type PinMarkerRendererProps = {
  /** Boolean to indicate if the pin marker is rendered in minimap */
  isInMinimap?: boolean;
};

/** @returns pin icon with or without pin marker action bar */
export function PinMarkerRenderer({
  isInMinimap = false,
}: PinMarkerRendererProps): JSX.Element | null {
  const pinContainer = useViewOverlayRef();
  const pinPosition = useAppSelector(selectPinMarker);

  const dispatch = useAppDispatch();

  const isNewPin = useAppSelector(selectIsNewPin);
  const [toolBarOpen, setToolBarOpen] = useState<boolean>(isNewPin);

  const onPinClick = useCallback(() => {
    if (!isInMinimap) {
      Analytics.track(
        toolBarOpen
          ? EventType.hidePinToolbarManually
          : EventType.showPinToolbar,
      );

      setToolBarOpen(!toolBarOpen);
      dispatch(setIsNewPin(false));
    }
  }, [dispatch, isInMinimap, toolBarOpen]);

  useEffect(
    () => () => {
      dispatch(setIsNewPin(false));
    },
    [dispatch],
  );

  return pinPosition ? (
    <>
      {(toolBarOpen || isNewPin) && !isInMinimap && (
        <PinMarkerActionBar
          pinPosition={pinPosition}
          parentRef={pinContainer}
        />
      )}

      <PinRenderer
        position={pinPosition}
        parentRef={pinContainer}
        onClick={onPinClick}
        isInMinimap={isInMinimap}
      />
    </>
  ) : null;
}

/** Size, in pixels, of the icon used to render the pin marker in main view*/
const PIN_ICON_SIZE = 35;

/** Size, in pixels, of the icon used to render the pin marker in minimap */
const PIN_ICON_MINI_SIZE = 18;

type PinRendererProps = {
  /** marker position in world space */
  position: Vector3Tuple;

  /** The parent that the label should have in the html DOM */
  parentRef: RefObject<HTMLElement>;

  /** Boolean to indicate if the pin marker is rendered in minimap */
  isInMinimap: boolean;

  /** Callback when this icon is clicked */
  onClick(): void;
};

/** @returns the pin icon placed in 3D at given position */
function PinRenderer({
  position,
  parentRef,
  isInMinimap,
  onClick,
}: PinRendererProps): JSX.Element {
  return (
    <Html
      zIndexRange={ANNOTATION_ZINDEX_RANGE_MAP.collapsed}
      eps={-1}
      position={position}
      portal={parentRef}
      style={{
        pointerEvents: isInMinimap ? "none" : "auto",
        transform: "translateY(-75%)",
      }}
    >
      <Box
        aria-label="pin-marker"
        component="div"
        sx={{
          pointerEvents: isInMinimap ? "none" : "auto",
          opacity: 1,
          cursor: "pointer",
        }}
        onClick={(ev) => {
          ev.stopPropagation();
          onClick();
        }}
      >
        <PinColoredIcon
          sx={{
            width: isInMinimap ? PIN_ICON_MINI_SIZE : PIN_ICON_SIZE,
            height: isInMinimap ? PIN_ICON_MINI_SIZE : PIN_ICON_SIZE,
          }}
        />
      </Box>
    </Html>
  );
}
